/**
 * English "near me" copy for the nine flagship clusters.
 *
 * Hand-authored (zero AI credits at build or runtime) and factually aligned
 * with the Thai registry in ./registry.ts:
 *  - Thailand is NOT a party to the Hague Apostille Convention, so
 *    overseas-bound Thai documents still pass the Legalization Division,
 *    Department of Consular Affairs (MFA) and, when the receiving authority
 *    asks for it, the destination country's embassy in Thailand.
 *  - No published prices (quote per case), no turnaround guarantees, and no
 *    promise of acceptance — acceptance is the receiving authority's decision.
 */

import type { NearServiceKey } from "./registry";

export type NearEnCopy = {
  /** Short English service label used in H1/title. */
  label: string;
  /** 1-2 sentence factual definition, used inside the answer-first block. */
  definition: string;
  /** Ordered working chain. */
  chain: string[];
  /** Documents the client should have ready. */
  checklist: string[];
  /** Frequent rejection causes. */
  pitfalls: string[];
  /** How much can be handled remotely. */
  remote: string;
};

import { NEAR_EN2_KEYS, NEAR_EN2_COPY } from "./en-copy-2";

const NEAR_EN1_KEYS = [
  "notary",
  "naati",
  "sworn",
  "pcc",
  "translation",
  "moj",
  "visa",
  "lgz",
  "apostille",
] as const satisfies readonly NearServiceKey[];

export const NEAR_EN_KEYS = [...NEAR_EN1_KEYS, ...NEAR_EN2_KEYS] as const;

export type NearEnKey = (typeof NEAR_EN_KEYS)[number];

const NEAR_EN1_COPY: Record<(typeof NEAR_EN1_KEYS)[number], NearEnCopy> = {
  notary: {
    label: "Notarial Services Attorney (Notary Public in Thailand)",
    definition:
      "Thailand has no separate notary public office. Signature and document certification is performed by a lawyer licensed by the Lawyers Council of Thailand as a Notarial Services Attorney, who certifies signatures, copies, affidavits and corporate authorisations for use abroad.",
    chain: [
      "Send colour scans of the full document set and name the receiving authority or country.",
      "We confirm which pages actually need certification and in what order.",
      "The signatory signs before the Notarial Services Attorney (in person or, where permitted, by the arrangement the receiving authority accepts).",
      "Certification wording, seal and register entry are completed.",
      "Where the destination requires it, the file continues to the Legalization Division (MFA) and then to the destination embassy in Thailand.",
      "Certified originals are delivered back by courier or collected at the office.",
    ],
    checklist: [
      "Passport or Thai ID of every signatory (valid, matching the document spelling).",
      "The document to be certified, unsigned, in final wording.",
      "Company affidavit / DBD extract and authorised-director list for corporate signings.",
      "Power of attorney where someone signs on another party's behalf.",
      "Written instructions from the receiving authority, if you already have them.",
    ],
    pitfalls: [
      "Signing the document before the certification appointment, which forces a fresh original.",
      "Name spelling that differs from the passport, the most common cause of rejection abroad.",
      "Assuming an Apostille exists for Thai documents — it does not; MFA legalization plus embassy certification is the usual route.",
      "Certifying a copy when the receiving authority asked for a certified original.",
      "Out-of-date corporate extracts, which many authorities require to be recent.",
    ],
    remote:
      "Document review, wording checks and the follow-on certification steps are handled remotely; the signature itself must be witnessed.",
  },
  naati: {
    label: "NAATI Certified Translation",
    definition:
      "A NAATI certified translation is produced by a translator credentialled by NAATI (the Australian authority for translators and interpreters) and carries the translator's certification statement and stamp, which Australian government bodies, universities and visa processing centres generally require.",
    chain: [
      "Send colour scans; we check legibility, seals and page count.",
      "Confirm the target authority (Home Affairs, a university, a licensing board) and the name spelling per passport.",
      "A NAATI-credentialled translator produces the translation with certification statement and stamp.",
      "Quality check against the source: names, dates, numbers, seal text.",
      "Delivery as a digital certified file and, on request, paper originals by post.",
    ],
    checklist: [
      "Passport page for the exact Latin spelling of every name.",
      "Full-colour scan of each document including reverse sides and seals.",
      "Any earlier translation already submitted, to keep terminology consistent.",
      "The receiving authority's translation requirements, if provided to you.",
    ],
    pitfalls: [
      "Cropped scans that cut off official seals, which invalidates the translation set.",
      "Different spellings of the same name across documents.",
      "Translating a document that the authority wanted in certified original form first.",
      "Sending greyscale copies where the seal colour cannot be verified.",
    ],
    remote: "The entire process is online — no travel required.",
  },
  sworn: {
    label: "Sworn / Court-Registered Translator",
    definition:
      "Many jurisdictions accept only a sworn or officially registered translator's work. In Thailand this is delivered by translators registered with the relevant authority or court list, whose certification page identifies the translator and the source document.",
    chain: [
      "Confirm which register the destination recognises (court list, embassy list, or ministry list).",
      "Scan review for legibility and completeness.",
      "Translation and certification page prepared by the registered translator.",
      "Optional onward certification at MFA and the destination embassy.",
      "Return delivery of stamped paper sets.",
    ],
    checklist: [
      "Passport or ID for name transliteration.",
      "Complete scans, including annexes and stamps.",
      "Name of the receiving authority and the language pair required.",
      "Any deadline set by the receiving authority.",
    ],
    pitfalls: [
      "Using a general translator where the destination accepts only a registered one.",
      "Skipping the certification page, which is the part authorities check.",
      "Mixed transliteration systems between documents.",
      "Missing annex pages that the source document references.",
    ],
    remote: "Most steps are handled on your behalf; only signature-in-person steps require attendance.",
  },
  pcc: {
    label: "Police Clearance Certificate (Thailand)",
    definition:
      "A Thai Police Clearance Certificate is issued by the Royal Thai Police and records whether criminal history appears in the Thai database. Visa, residence, licensing and employment authorities abroad commonly require it, usually together with a certified translation.",
    chain: [
      "Confirm the requesting authority and the purpose stated on the application.",
      "Prepare the application file: identity documents, purpose evidence, photographs.",
      "Fingerprinting, which must be done in person or via an accepted overseas arrangement.",
      "Submission to the Royal Thai Police and status follow-up.",
      "Certified translation, then MFA legalization and embassy certification where required.",
    ],
    checklist: [
      "Passport (and Thai ID or work permit if applicable).",
      "Evidence of purpose — visa letter, employer request, or application form from the authority.",
      "Recent photographs to the specification requested.",
      "Proof of address or immigration documents where the applicant is a foreign national.",
      "Power of attorney if the file is submitted on the applicant's behalf.",
    ],
    pitfalls: [
      "Stating a purpose that does not match the requesting authority, which can invalidate the certificate.",
      "Fingerprint cards taken outside the accepted format.",
      "Certificates issued too long before submission, when the authority sets a validity window.",
      "Forgetting that the translation and legalization chain adds separate processing time.",
    ],
    remote:
      "Fingerprinting must be done in person; filing, follow-up, translation and legalization are handled for you.",
  },
  translation: {
    label: "Certified Translation (all languages)",
    definition:
      "A certified translation is a complete translation accompanied by a signed certification that it corresponds to the source document. Which certifier is acceptable — agency, registered translator, embassy-listed translator — is set by the receiving authority.",
    chain: [
      "Scan review and quotation per case.",
      "Terminology and name transliteration fixed against the passport.",
      "Translation, then independent proofreading against the source.",
      "Certification page issued and stamped.",
      "Optional MFA legalization and destination-embassy certification.",
    ],
    checklist: [
      "Colour scans of every page, front and back.",
      "Passport page for name spelling.",
      "Target language and receiving authority.",
      "Previous translations, so wording stays consistent.",
    ],
    pitfalls: [
      "Partial translation of a multi-page document.",
      "Translating before an official seal is added, so the seal never appears in the translation.",
      "Mismatched names between certificate, passport and application form.",
      "Assuming one certification satisfies every country — requirements differ.",
    ],
    remote: "Fully online, with paper sets posted where originals are needed.",
  },
  moj: {
    label: "Ministry of Justice and government document services",
    definition:
      "Ministry of Justice and related government filings cover records, applications and certifications handled by Thai authorities. Each has its own form set, evidence list and queue, so the correct sequence matters more than speed.",
    chain: [
      "Identify the exact authority, division and form required.",
      "Assemble the evidence list and check identity documents.",
      "File the application and record the reference number.",
      "Follow up on status and respond to any request for further evidence.",
      "Certified translation and legalization where the document leaves Thailand.",
    ],
    checklist: [
      "Passport or Thai ID of the applicant.",
      "Household registration or corporate documents, depending on the filing.",
      "Supporting evidence named in the authority's form.",
      "Power of attorney where an agent files for you.",
    ],
    pitfalls: [
      "Filing to the wrong division, which restarts the queue.",
      "Incomplete evidence lists, the most frequent cause of delay.",
      "Ignoring validity windows on supporting certificates.",
      "Translating before the Thai original is final.",
    ],
    remote: "Filing and follow-up are handled on your behalf where the authority permits representation.",
  },
  visa: {
    label: "Visa and immigration document preparation",
    definition:
      "Visa document work is document preparation and advisory: assembling, translating and certifying the file a consulate or immigration office asks for. The decision itself always belongs to the authority.",
    chain: [
      "Confirm the visa category and the current published document list.",
      "Gap analysis against what you already hold.",
      "Certified translations and certifications for the documents that need them.",
      "File assembly in the order the authority lists it, with a submission checklist.",
      "Support during submission and response to any additional-document request.",
    ],
    checklist: [
      "Passport with sufficient validity and previous visa pages.",
      "Civil status documents — marriage, birth, divorce — where the category requires them.",
      "Financial evidence in the format the authority specifies.",
      "Employment, study or sponsorship letters.",
      "Police clearance and medical documents where required.",
    ],
    pitfalls: [
      "Using an outdated document list from a blog rather than the authority's current one.",
      "Financial evidence in the wrong format or period.",
      "Documents certified in the wrong order, so the certification does not cover the final version.",
      "Expecting an approval guarantee — no adviser can give one.",
    ],
    remote: "Preparation and review are remote; interviews and biometrics are attended in person.",
  },
  lgz: {
    label: "MFA legalization (Department of Consular Affairs)",
    definition:
      "Legalization at the Legalization Division, Department of Consular Affairs (Ministry of Foreign Affairs) certifies the signature and seal on Thai official documents and their translations. Because Thailand is not an Apostille state, this is the standard step before a destination embassy certifies the file.",
    chain: [
      "Confirm whether the destination wants the Thai original, the translation, or both legalized.",
      "Obtain fresh official originals where the authority requires recent issuance.",
      "Certified translation prepared so that seals and stamps are also rendered.",
      "Submission to the Legalization Division and collection.",
      "Destination-embassy certification where the receiving authority requires it.",
    ],
    checklist: [
      "Official Thai originals bearing signature and seal.",
      "Passport or ID of the document holder.",
      "Certified translation, if the destination requires a translated set.",
      "Power of attorney for submission on your behalf.",
    ],
    pitfalls: [
      "Expecting an Apostille for a Thai document — Thailand is not a party to that convention.",
      "Translations that omit seal and stamp text.",
      "Photocopies where the division requires the issuing authority's original.",
      "Legalizing before the destination confirms which set it wants, causing duplicate rounds.",
    ],
    remote: "Submission and collection are handled for you; you send the originals in.",
  },
  apostille: {
    label: "Apostille vs legalization for Thai documents",
    definition:
      "An Apostille is a single certificate used between parties to the Hague Convention. Thailand is not a party, so Thai documents are instead legalized by the Department of Consular Affairs and, where required, certified by the destination country's embassy in Thailand. Foreign documents apostilled abroad may still need Thai translation and certification for use in Thailand.",
    chain: [
      "Establish the direction of use: Thai document going abroad, or foreign document coming into Thailand.",
      "For outbound files: certified translation, MFA legalization, then destination-embassy certification if required.",
      "For inbound files: verify the foreign Apostille, then arrange certified Thai translation and any Thai-side certification.",
      "Confirm the receiving authority's current wording requirements before submission.",
      "Deliver the completed set with a submission checklist.",
    ],
    checklist: [
      "The document in its current certified state, including any foreign Apostille.",
      "Passport of the holder for name consistency.",
      "Written requirements from the receiving authority where available.",
      "Details of the destination country and the purpose of use.",
    ],
    pitfalls: [
      "Paying for an 'Apostille' on a Thai document, which cannot be issued.",
      "Assuming a foreign Apostille removes the need for a Thai certified translation.",
      "Skipping embassy certification when the receiving authority still requires it.",
      "Relying on requirements that changed after the guide you read was written.",
    ],
    remote: "Assessment and the full certification chain are handled remotely; you courier originals to us.",
  },
};

export const NEAR_EN_COPY: Record<NearEnKey, NearEnCopy> = {
  ...NEAR_EN1_COPY,
  ...NEAR_EN2_COPY,
};

export function nearEnCopy(key: NearServiceKey): NearEnCopy | undefined {
  return (NEAR_EN_COPY as Record<string, NearEnCopy>)[key];
}
